import { LINK_PREVIEW_QUERY_KEY } from "@/src/query-keys/link-preview.keys";
import { resolveLinkPreview } from "@/src/services/api/link-preview/link-preview.service";
import {
  detectMediaKind,
  extractFirstUrl,
  normalizeUrl,
} from "@/src/utils/link-preview.utils";
import { useQuery } from "@tanstack/react-query";

export { LINK_PREVIEW_QUERY_KEY };

/**
 * Derive a link preview for the first URL found in `content`.
 *
 * Resolution is async and never blocks the caller: `data === undefined` while
 * it is in flight, `data === null` when there is nothing worth showing. Callers
 * render the post either way — the URL is already in the body text as a link.
 *
 * The query is disabled for URLs that point straight at an image or a video
 * file; those render inline from the URL with no metadata lookup at all.
 */
export const useLinkPreview = (
  content: string,
  options: { enabled?: boolean } = {},
) => {
  const url = extractFirstUrl(content);
  const kind = url ? detectMediaKind(url) : null;

  return useQuery({
    // Normalised, so two posts sharing the same article with different tracking
    // parameters share one cache entry.
    queryKey: [LINK_PREVIEW_QUERY_KEY, url ? normalizeUrl(url) : null],
    queryFn: () => resolveLinkPreview(url!),
    enabled: (options.enabled ?? true) && !!url && kind === "page",
    // Metadata is stable and already cached server-side; this only avoids
    // re-asking while the user scrolls the same feed.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    // A failed preview is not worth retrying; degrade immediately to plain link.
    retry: 0,
  });
};
